import { Download, FileText } from 'lucide-react';
import ScrollReveal from './ScrollReveal.jsx';
import { vacacionesReglamento as reg } from '../../content/vacacionesReglamento.js';
import { formatMoneyCOP } from '../../utils/formatMoney.js';

function TarifaHabitacionTable() {
  const { tarifasHabitacion: t } = reg;
  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
      <table className="w-full min-w-[28rem] text-left text-sm">
        <thead>
          <tr className="bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-600">
            <th className="px-4 py-3">Estadía</th>
            <th className="px-4 py-3">Lun — Jue</th>
            <th className="px-4 py-3">Vie — Dom / festivo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {t.filas.map((fila) => (
            <tr key={fila.tipo}>
              <td className="px-4 py-3 font-medium text-slate-800">{fila.tipo}</td>
              <td className="px-4 py-3 text-slate-700">{formatMoneyCOP(fila.lunJue)}</td>
              <td className="px-4 py-3 text-slate-700">{formatMoneyCOP(fila.vieDom)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TarifaCasasTable() {
  const { tarifasCasas: t } = reg;
  return (
    <div className="overflow-x-auto rounded-xl ring-1 ring-slate-200">
      <table className="w-full min-w-[32rem] text-left text-sm">
        <thead>
          <tr className="bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-600">
            <th className="px-4 py-3">Casa</th>
            <th className="px-4 py-3">Asociados</th>
            <th className="px-4 py-3">No asociados</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200 bg-white">
          {t.casas.map((casa) => (
            <tr key={casa.nombre}>
              <td className="px-4 py-3 font-medium text-slate-800">{casa.nombre}</td>
              <td className="px-4 py-3 text-slate-700">
                {formatMoneyCOP(casa.asociados.valor)} / noche
                <span className="mt-0.5 block text-xs text-slate-500">Cupo máx. {casa.asociados.cupo} personas</span>
              </td>
              <td className="px-4 py-3 text-slate-700">
                {formatMoneyCOP(casa.noAsociados.valor)} / noche
                <span className="mt-0.5 block text-xs text-slate-500">Cupo máx. {casa.noAsociados.cupo} personas</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function VacacionesReglamentoPanel({ className = '' }) {
  const { meta, horarios, reserva, tarifasHabitacion, tarifasCasas, pasadia, beneficios, normasDestacadas } =
    reg;

  return (
    <div className={className}>
      <ScrollReveal>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:p-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.28em] text-accent-lime">Normativa vigente</p>
              <h2 className="mt-2 font-display text-2xl font-bold text-accent-blue md:text-3xl">{meta.titulo}</h2>
              <p className="mt-3 text-sm text-slate-600">
                {meta.acuerdo} · Código {meta.codigo} v.{meta.version} · Actualizado {meta.fechaActualizacion}
              </p>
              <p className="mt-4 text-base leading-relaxed text-slate-600">{reserva.formulario}</p>
              <p className="mt-2 text-sm text-slate-500">{reserva.aprobacion}</p>
            </div>
            <a
              href={meta.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-accent-blue px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0d2b7f]"
            >
              <Download className="h-4 w-4" aria-hidden />
              Descargar reglamento (PDF)
            </a>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Check-in</p>
              <p className="mt-1 font-semibold text-slate-800">{horarios.checkIn}</p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Check-out</p>
              <p className="mt-1 font-semibold text-slate-800">{horarios.checkOut}</p>
            </div>
            <div className="rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-slate-200 sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Piscina</p>
              <p className="mt-1 text-sm font-semibold text-slate-800">{horarios.piscina}</p>
            </div>
          </div>

          <div className="mt-12 space-y-4">
            <h3 className="flex items-center gap-2 font-display text-xl font-semibold text-accent-blue">
              <FileText className="h-5 w-5 text-accent-lime" aria-hidden />
              {tarifasHabitacion.titulo}
            </h3>
            <TarifaHabitacionTable />
            <p className="text-sm text-slate-600">{tarifasHabitacion.nota}</p>
            <ul className="list-inside list-disc space-y-1 text-sm text-slate-600">
              {tarifasHabitacion.pago.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
            <p className="text-sm text-slate-500">{tarifasHabitacion.capacidad}</p>
          </div>

          <div className="mt-12 space-y-4">
            <h3 className="font-display text-xl font-semibold text-accent-blue">{tarifasCasas.titulo}</h3>
            <TarifaCasasTable />
            <p className="text-sm text-slate-500">{tarifasCasas.capacidadMax}</p>
          </div>

          <div className="mt-10 flex flex-wrap gap-4 rounded-xl border border-[#1acb12]/30 bg-[#1acb12]/5 px-5 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{pasadia.titulo}</p>
              <p className="mt-1 font-display text-2xl font-bold text-accent-lime">{formatMoneyCOP(pasadia.valor)}</p>
            </div>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <div>
              <h3 className="font-display text-lg font-semibold text-accent-blue">Beneficios y pagos</h3>
              <ul className="mt-3 list-inside list-disc space-y-2 text-sm leading-relaxed text-slate-600">
                {beneficios.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-accent-blue">Normas destacadas</h3>
              <ul className="mt-3 list-inside list-disc space-y-2 text-sm leading-relaxed text-slate-600">
                {normasDestacadas.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-8 text-xs text-slate-500">{reserva.responsable}</p>
        </div>
      </ScrollReveal>
    </div>
  );
}
